import type { Vec2, Vec3 } from '../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import { layoutToWorld } from '../../core/coords';
import { WORLD } from '../../core/config';
import type { bridgeProfile } from '../../site/bridgeHeight';

const DECK_TOP = WORLD.groundY;
const PARAPET = { height: 0.6, thickness: 0.3 } as const;

export function buildParapets(
  builder: MeshBuilder,
  style: VertexStyle,
  sides: readonly { a: Vec2; b: Vec2 }[],
  profile: ReturnType<typeof bridgeProfile>
): void {
  for (const side of sides) {
    const frame: Frame = {
      origin: layoutToWorld(side.a, DECK_TOP),
      yaw: Math.atan2(side.b[1] - side.a[1], side.b[0] - side.a[0]),
    };
    const length = Math.hypot(side.b[0] - side.a[0], side.b[1] - side.a[1]);
    const start = profile.along(side.a);
    const direction = profile.along(side.b) >= start ? 1 : -1;
    const cuts = [
      0,
      ...profile.knots
        .map((x) => (x - start) * direction)
        .filter((x) => x > 1e-5 && x < length - 1e-5),
      length,
    ].sort((a, b) => a - b);
    for (let i = 1; i < cuts.length; i++) {
      const from = cuts[i - 1]!,
        to = cuts[i]!;
      const ya = profile.height(start + direction * from) - DECK_TOP;
      const yb = profile.height(start + direction * to) - DECK_TOP;
      const d = PARAPET.thickness,
        h = PARAPET.height;
      const a: Vec3 = [from, ya, 0],
        b: Vec3 = [to, yb, 0],
        c: Vec3 = [to, yb, d],
        e: Vec3 = [from, ya, d];
      const up = (p: Vec3): Vec3 => [p[0], p[1] + h, p[2]];
      builder.quad(style, frame, [up(a), up(b), b, a]);
      builder.quad(style, frame, [up(c), up(e), e, c]);
      builder.quad(style, frame, [up(e), up(a), a, e]);
      builder.quad(style, frame, [up(b), up(c), c, b]);
      builder.quad(style, frame, [up(e), up(c), up(b), up(a)]);
      builder.quad(style, frame, [a, b, c, e]);
    }
  }
}
